import { fetchAPI, METHODS, assertErrors, parseBody } from './common';
import _toLower from 'lodash/toLower';
import _map from 'lodash/map';
import qs from 'qs';

/**
 * @typedef User
 */

/**
 * @typedef Org
 */

/**
 * Gets all users in database.
 * @returns Promise<User[]>
 */
export async function getAllUsers({
  offset,
  step,
  filterModel,
  name,
  isActive,
  isDeleted,
  emailVerified,
  inviteAccepted,
  noTeams,
}) {
  const queryObj = {
    offset,
    step,
    name,
    isActive,
    isDeleted,
    emailVerified,
    inviteAccepted,
    noTeams,
  };
  const res = await fetchAPI(
    `/admin/users?${qs.stringify(queryObj)}`,
    METHODS.post,
    { filterModel }
  );
  const { users } = await parseBody(res);
  return users;
}

/**
 * Updates a user by their id.
 * @param {string} id
 * @param {Partial<User>} user
 */
export async function updateUser(id, user) {
  const res = await fetchAPI('/admin/users', METHODS.put, {
    user: { ...user, id },
  });
  await parseBody(res);
}

export async function changeUserPassword(userId, password) {
  const res = await fetchAPI(`/admin/user/${userId}/password`, METHODS.post, {
    password,
  });
  return await parseBody(res);
}

/**
 * Adds a user's email to the email blacklist.
 * @param {string} userId
 */
export async function blacklistUserEmail(userId) {
  const res = await fetchAPI(
    `/admin/user/${userId}/blacklist-email`,
    METHODS.post
  );
  return await parseBody(res);
}

/**
 * Remove a user from a team.
 * @param {object} params
 * @param {string} params.userId
 * @param {string} params.teamId
 */
export async function removeTeamUser({ userId, teamId }) {
  const res = await fetchAPI(`/admin/team/${teamId}/${userId}`, METHODS.delete);
  await assertErrors(res);
}

/**
 * Deactivates an existing user.
 * @param {string} id
 */
export async function deactivateUser(id) {
  const res = await fetchAPI('/admin/deactivate-user', METHODS.post, { id });
  await parseBody(res);
}

/**
 * Revokes all active sessions for a user.
 * @param {string} id
 */
export async function revokeUserSessions(id) {
  const res = await fetchAPI('/admin/revoke-user-sessions', METHODS.post, {
    id,
  });
  await parseBody(res);
}

/**
 * Activates an existing user.
 * @param {string} id
 */
export async function activateUser(id) {
  const res = await fetchAPI('/admin/activate-user', METHODS.post, { id });
  await parseBody(res);
}

/**
 * Gets all organizations.
 * @returns Promise<Org[]>
 */
export async function getAllOrgs({
  page,
  pageSize,
  filterTagsToInclude,
  filterTagsToExclude,
  city,
  province,
  formattedAddress,
  name,
  type,
  isApproved,
  isVisible,
  plan,
  noAdmins,
  accountTeamId,
  accountType,
  filterModel,
}) {
  const includeTags = _map(filterTagsToInclude, (tag) => _toLower(tag.label));
  const excludeTags = _map(filterTagsToExclude, (tag) => _toLower(tag.label));
  const queryObj = {
    page,
    pageSize,
    includeTags,
    excludeTags,
    city,
    province,
    formattedAddress,
    name,
    type,
    isApproved,
    isVisible,
    plan,
    noAdmins,
    accountTeamId,
    accountType,
  };
  const queryString = qs.stringify(queryObj);
  const res = await fetchAPI(
    `/admin/orgs/get${queryString ? `?${queryString}` : ''}`,
    METHODS.post,
    { filterModel }
  );

  const { orgs } = await parseBody(res);
  return orgs;
}

/**
 * Gets all organizations.
 * @returns Promise<Org[]>
 */
export async function getAllOrgsSubs({
  step,
  offset,
  name,
  city,
  province,
  planTypes,
  usesStripe,
  expiring,
}) {
  const queryObj = {
    step,
    offset,
    name,
    city,
    province,
    planTypes,
    usesStripe,
    expiring,
  };
  const res = await fetchAPI(
    `/admin/orgs-subs?${qs.stringify(queryObj)}`,
    METHODS.get
  );

  const { orgs } = await parseBody(res);
  return orgs;
}

/**
 * Gets summary KPI counts for all org subscriptions.
 * @returns Promise<Object>
 */
export async function getOrgsSubsSummary() {
  const res = await fetchAPI(`/admin/orgs-subs/summary`, METHODS.get);

  const { summary } = await parseBody(res);
  return summary;
}

/**
 * Bulk-assigns an artificial plan to a batch of organizations. Organizations
 * whose current plan is stripe-backed are skipped and returned in `skipped`.
 * @returns Promise<{updated: string[], skipped: string[]}>
 */
export async function bulkSetOrgsPlan(teamIds, productKey) {
  const res = await fetchAPI(`/admin/orgs-subs/bulk-plan`, METHODS.post, {
    teamIds,
    productKey,
  });
  return await parseBody(res);
}

/**
 * Bulk-attaches tags to a batch of organizations. Organizations that already
 * have all requested tags are skipped and returned in `skipped`.
 * @returns Promise<{updated: string[], skipped: string[], failed: string[]}>
 */
export async function bulkAttachOrgTags(teamIds, tags) {
  const res = await fetchAPI(`/admin/orgs/bulk-tags`, METHODS.post, {
    teamIds,
    tags,
  });
  return await parseBody(res);
}

/**
 * Bulk-adds organizations to one corporate account. Skips come back as
 * `{ teamId, reason }` (`already_linked` or `not_business`).
 * @returns Promise<{updated: string[], skipped: {teamId: string, reason: string}[], failed: string[]}>
 */
export async function bulkAddOrgsToCorporate({ corpTeamId, teamIds }) {
  const res = await fetchAPI(`/admin/orgs/bulk-corporate`, METHODS.post, {
    corpTeamId,
    teamIds,
  });
  return await parseBody(res);
}

/**
 * Bulk-removes an artificial plan from a batch of organizations, allowing
 * them to switch to a stripe-powered plan. Organizations whose current plan
 * is stripe-backed are skipped and returned in `skipped`.
 * @returns Promise<{updated: string[], skipped: string[]}>
 */
export async function bulkRemoveOrgsPlan(teamIds) {
  const res = await fetchAPI(
    `/admin/orgs-subs/bulk-remove-plan`,
    METHODS.post,
    { teamIds }
  );
  return await parseBody(res);
}

export async function getFreshcueNotificationWaves({
  offset = 0,
  step = 25,
  filterModel,
} = {}) {
  const queryString = qs.stringify({ offset, step });
  const res = await fetchAPI(
    `/admin/freshcue-notification-waves?${queryString}`,
    METHODS.post,
    { filterModel }
  );
  const { waves, count } = await parseBody(res);
  return { waves, count };
}

export async function getFreshcueWaitlistEntries({
  offset = 0,
  step = 25,
  filterModel,
} = {}) {
  const queryString = qs.stringify({ offset, step });
  const res = await fetchAPI(
    `/admin/freshcue-waitlist?${queryString}`,
    METHODS.post,
    { filterModel }
  );
  const { entries, count } = await parseBody(res);
  return { entries, count };
}

export async function getDistributionOrgActivity({ date }) {
  const queryString = qs.stringify({ date });
  const res = await fetchAPI(
    `/admin/distributions/org-activity?${queryString}`,
    METHODS.get
  );
  return parseBody(res);
}

export async function getDistributionOrgRuns({ teamId, date }) {
  const queryString = qs.stringify({ date });
  const res = await fetchAPI(
    `/admin/distributions/orgs/${teamId}/runs?${queryString}`,
    METHODS.get
  );
  return parseBody(res);
}

/**
 * Gets all organizations.
 * @returns Promise<Org[]>
 */
export async function downloadAllOrgs({
  filterTagsToInclude = [],
  filterTagsToExclude = [],
  city = '',
  province = '',
  name = '',
  type = '',
  isApproved = '',
  isVisible = '',
  plan = '',
  noAdmins = '',
  accountTeamId = '',
  accountType = '',
  search = '', // Searches for name and formatted address
}) {
  const includeTags = _map(filterTagsToInclude, (tag) => _toLower(tag.label));
  const excludeTags = _map(filterTagsToExclude, (tag) => _toLower(tag.label));
  const queryObj = {
    includeTags,
    excludeTags,
    city,
    province,
    name,
    type,
    isApproved,
    isVisible,
    plan,
    noAdmins,
    accountTeamId,
    accountType,
    search,
  };
  const res = await fetchAPI(
    `/admin/orgs/download?${qs.stringify(queryObj)}`,
    METHODS.get
  );

  const { orgs } = await parseBody(res);
  return orgs;
}

/**
 * Updates an existing organization by id.
 * @param {string} id
 * @param {Partial<Org>} org
 */
export async function updateOrg(id, org) {
  const res = await fetchAPI('/admin/orgs', METHODS.put, {
    org: { ...org, teamId: id },
  });
  await assertErrors(res);
}

/**
 * Updates an existing executive organization by id.
 * @param {string} id
 * @param {Partial<Org>} org
 */
export async function updateExecutiveOrg(id, org) {
  const res = await fetchAPI('/admin/orgs/executive', METHODS.put, {
    org: { ...org, teamId: id },
  });
  await assertErrors(res);
}

/**
 * Updates an existing executive organization by id.
 * @param {string} id
 * @param {Partial<Org>} org
 */
export async function updateCorporateOrg(id, org) {
  const res = await fetchAPI('/admin/orgs/corporate', METHODS.put, {
    org: { ...org, teamId: id },
  });
  await assertErrors(res);
}

/**
 * Creates a new organization.
 * @param {Org} org
 */
export async function createOrg(org) {
  const res = await fetchAPI('/admin/orgs', METHODS.post, { org });
  await assertErrors(res);
}

export async function getTeam(teamId, { includeLastActivity = false } = {}) {
  const res = await fetchAPI(
    `/admin/team/${teamId}?${qs.stringify({ includeLastActivity })}`,
    METHODS.get
  );
  const { team } = await parseBody(res);
  return team;
}

export async function getTeamPaginated({
  teamId,
  offset,
  step,
  filterModel,
  includeLastActivity = false,
}) {
  const res = await fetchAPI(
    `/admin/team/${teamId}/list?${qs.stringify({
      offset,
      step,
      includeLastActivity,
    })}`,
    METHODS.post,
    { filterModel }
  );
  return await parseBody(res);
}

/**
 * Set a member's job title in the given organization. A blank title clears it.
 * @param {{teamId: string, userId: string, jobTitle: string}} params
 */
export async function updateTeamUserJobTitle({ teamId, userId, jobTitle }) {
  const res = await fetchAPI('/admin/team/user/job-title', METHODS.put, {
    teamId,
    userId,
    jobTitle,
  });
  return await parseBody(res);
}

export async function updateTeamUser(userId, { isAdmin, isDriver, teamId }) {
  const res = await fetchAPI('/admin/team/user', METHODS.put, {
    userId,
    isAdmin,
    isDriver,
    teamId,
  });
  await parseBody(res);
}

export async function getAllFoodRuns({ offset, step, filterModel }) {
  const res = await fetchAPI(
    `/admin/food-runs/get?${qs.stringify({ offset, step })}`,
    METHODS.post,
    { filterModel }
  );
  return await parseBody(res);
}

export async function getAllDonations({ offset, step, filterModel }) {
  const res = await fetchAPI(
    `/admin/donations/get?${qs.stringify({ offset, step })}`,
    METHODS.post,
    { filterModel }
  );
  return await parseBody(res);
}

export async function getAllRescues({ offset, step, filterModel }) {
  const res = await fetchAPI(
    `/admin/rescues/get?${qs.stringify({ offset, step })}`,
    METHODS.post,
    { filterModel }
  );
  return await parseBody(res);
}

export async function impersonateUser(userId) {
  const res = await fetchAPI('/admin/impersonate-user', METHODS.post, {
    userId,
  });
  const { user } = await parseBody(res);
  return user;
}

/**
 * Creates a one-time link that opens the user's session on the white-label
 * dashboard of the brand `teamId` belongs to.
 * @returns {Promise<{ url: string, expiresAt: string }>}
 */
export async function createImpersonationHandoff({ userId, teamId }) {
  const res = await fetchAPI('/admin/impersonation-handoffs', METHODS.post, {
    userId,
    teamId,
  });
  return parseBody(res);
}

/**
 * Get the plan for an organisation. (May be stripe powered or artificial)
 */
export async function getTeamPlan(teamId) {
  const res = await fetchAPI(`/admin/plan/${teamId}`, METHODS.get);
  const { plan } = await parseBody(res);
  return plan;
}

export async function getOrgConnectAccountStatus(teamId) {
  const res = await fetchAPI(
    `/admin/orgs/${teamId}/connect-status`,
    METHODS.get
  );
  return await parseBody(res);
}

/**
 * Update an artificial plan for an organisation.
 *
 * `productKey` is the canonical plan key (e.g. "foodMover"). The backend
 * derives the actual Stripe product/price ids server-side from this key —
 * it never trusts a client-supplied Stripe id (see `updateTeamPlan` in
 * `apps/backend/src/controllers/admin.js` for the security rationale).
 */
export async function updateTeamPlan(teamId, status, productKey) {
  const res = await fetchAPI(`/admin/plan/${teamId}`, METHODS.post, {
    status,
    productKey,
  });
  await assertErrors(res);
}

/**
 * Super-admin: check if Food Mover Lite/Pro org can take Home Delivery add-on.
 */
export async function getHomeDeliveryAddonEligibility(teamId) {
  const res = await fetchAPI(
    `/admin/plan/${teamId}/home-delivery-addon`,
    METHODS.get
  );
  const body = await parseBody(res);
  return body.eligibility;
}

/**
 * Super-admin: add Home Delivery (+$250) to Food Mover Lite or Pro.
 */
export async function addHomeDeliveryAddon(teamId) {
  const res = await fetchAPI(
    `/admin/plan/${teamId}/home-delivery-addon`,
    METHODS.post
  );
  await assertErrors(res);
  return await parseBody(res);
}

/**
 * Super-admin: remove Home Delivery add-on (downgrade to base Food Mover plan).
 */
export async function removeHomeDeliveryAddon(teamId) {
  const res = await fetchAPI(
    `/admin/plan/${teamId}/home-delivery-addon`,
    METHODS.delete
  );
  await assertErrors(res);
  return await parseBody(res);
}

/**
 * Delete an artificial plan such that the organisation can switch to a stripe
 * plan.
 */
export async function deleteTeamPlan(teamId) {
  await fetchAPI(`/admin/plan/${teamId}`, METHODS.delete);
}

/**
 * Get the recent admin-driven plan grant/change/removal history for an
 * organisation (newest first).
 */
export async function getTeamPlanActivity(teamId) {
  const res = await fetchAPI(`/admin/plan/${teamId}/activity`, METHODS.get);
  const body = await parseBody(res);
  return {
    activity: body.activity ?? [],
    invoices: body.invoices ?? [],
    stripe: body.stripe ?? null,
  };
}

/**
 * Delete a donation
 *
 */
export async function deleteDonation(donationId) {
  return await fetchAPI(`/admin/donation/${donationId}`, METHODS.delete);
}

/**
 * Permanently Delete a user
 *
 */
export async function deleteUser(userId) {
  const res = await fetchAPI(`/admin/user/${userId}`, METHODS.delete);
  return await parseBody(res);
}

/**
 * Permanently Delete an org
 *
 */
export async function deleteOrg(teamId) {
  await fetchAPI(`/admin/org/${teamId}`, METHODS.delete);
}

/**
 * Add tags to an org
 *
 */
export async function addOrgTags(data) {
  await fetchAPI(`/admin/org/tag`, METHODS.post, data);
}

/**
 * Get tag suggestions
 *
 */
export async function getTags() {
  const res = await fetchAPI(`/admin/org/tag`, METHODS.get);
  const data = await parseBody(res);
  return data;
}

export async function getOrgTagsAdmin() {
  const res = await fetchAPI('/admin/org/tags', METHODS.get);
  const { tags } = await parseBody(res);
  return tags;
}

export async function createOrgTag(name) {
  const res = await fetchAPI('/admin/org/tags', METHODS.post, { name });
  return await parseBody(res);
}

export async function updateOrgTag(id, name) {
  const res = await fetchAPI(`/admin/org/tags/${id}`, METHODS.put, { name });
  return await parseBody(res);
}

export async function deleteOrgTag(id) {
  const res = await fetchAPI(`/admin/org/tags/${id}`, METHODS.delete);
  return await parseBody(res);
}

export async function getPlanNames() {
  const res = await fetchAPI(`/admin/plan-names`, METHODS.get);
  const data = await parseBody(res);
  return data;
}

/**
 * Get all executive users.
 * @returns ExecutiveAccount[]
 */
export async function getAllExecutiveAccounts() {
  const res = await fetchAPI(`/admin/executive-accounts`, METHODS.get);
  const { users } = await parseBody(res);
  return users;
}

/**
 * Add an executive user.
 * The new user will get an email instructing them how to access their new account.
 * @param {object} param0
 * @param {string} param0.name
 * @param {string} param0.email
 * @param {string} param0.userFirstName
 * @param {string} param0.userLastName
 * @param {string} param0.userEmail
 * @param {string} param0.description
 * @param {string} param0.country
 * @param {string} param0.province
 * @param {string} param0.timezone_id
 * @param {string} param0.api_enabled
 */
export async function addExecutiveAccount({
  name,
  email,
  legalName,
  userFirstName,
  userLastName,
  userEmail,
  description,
  country,
  province,
  timezone_id,
  api_enabled,
  inspections_enabled,
  complaints_enabled,
  nov_enabled,
  penalties_enabled,
  scheduling_enabled,
  contact_log_enabled,
  donations_enabled,
}) {
  await fetchAPI(`/admin/executive-account`, METHODS.post, {
    name,
    legalName,
    email,
    userFirstName,
    userLastName,
    userEmail,
    country,
    province,
    timezone_id,
    description,
    api_enabled,
    inspections_enabled,
    complaints_enabled,
    nov_enabled,
    penalties_enabled,
    scheduling_enabled,
    contact_log_enabled,
    donations_enabled,
  });
}

/**
 * @typedef Geofence
 * @property {string} id
 * @property {string} name
 * @property {Point[][]} points
 * @property {AABB} aabb
 */

/**
 * @typedef Point
 * @property {number} lat
 * @property {number} lng
 */

/**
 * @typedef AABB
 * @property {Point} min
 * @property {Point} max
 */

/**
 * Get all geofences for the executive user.
 * @param {string} userId
 */
export async function getAllGeofences(userId) {
  const res = await fetchAPI(`/admin/geofences/${userId}`, METHODS.get);
  const { geofences } = await parseBody(res);
  return geofences;
}

/**
 * Add a geofence for an executive user.
 * @param {string} teamId
 * @param {Geofence} geofence
 */
export async function addGeofence(teamId, geofence) {
  await fetchAPI(`/admin/geofences/${teamId}`, METHODS.post, geofence);
}

/**
 * Create multiple geofences to an executive user (in bulk).
 * Indexing happens in the background; poll the returned job id for progress.
 * @param {string} teamId
 * @param {Geofence[]} geofences
 * @returns Promise<{ geofenceIds: string[], jobId: string|null }>
 */
export async function addGeofences(teamId, geofences) {
  const res = await fetchAPI(`/admin/geofences/bulk/${teamId}`, METHODS.post, {
    geofences,
  });
  await assertErrors(res);
  return parseBody(res);
}

/**
 * @typedef GeofenceIndexJob
 * @property {string} id
 * @property {'PENDING'|'RUNNING'|'COMPLETED'|'FAILED'} status
 * @property {number} total
 * @property {number} processed
 * @property {string|null} error
 */

/**
 * Fetch one geofence index job for an executive team.
 * @param {string} teamId
 * @param {string} jobId
 * @returns Promise<GeofenceIndexJob>
 */
export async function getGeofenceIndexJob(teamId, jobId) {
  const res = await fetchAPI(
    `/admin/geofences/${teamId}/index-jobs/${jobId}`,
    METHODS.get
  );
  await assertErrors(res);
  const { job } = await parseBody(res);
  return job;
}

/**
 * Re-run a failed geofence index job.
 * @param {string} teamId
 * @param {string} jobId
 * @returns Promise<{ job: GeofenceIndexJob, retried: boolean }>
 */
export async function retryGeofenceIndexJob(teamId, jobId) {
  const res = await fetchAPI(
    `/admin/geofences/${teamId}/index-jobs/${jobId}/retry`,
    METHODS.post,
    {}
  );
  await assertErrors(res);
  return parseBody(res);
}

/**
 * Get all agreements concerning the organization of `teamId`.
 * @param {string} teamId
 * @returns Promise<Agreement[]>
 */
export async function getAgreementsForOrg({
  teamId,
  offset,
  step,
  filterModel,
}) {
  const res = await fetchAPI(
    `/admin/agreements/${teamId}?${qs.stringify({ offset, step })}`,
    METHODS.post,
    { filterModel }
  );
  const { agreements, count } = await parseBody(res);
  return { agreements, count };
}

/**
 * Rename a geofence.
 * @param {string} id
 * @param {string} name
 * @param {string} teamId
 */
export async function renameGeofence(id, name, teamId) {
  await fetchAPI(`/admin/geozones/${teamId}/${id}`, METHODS.put, {
    name,
  });
}

/**
 * Update a geofence.
 * @param {string} id
 * @param {string} name
 * @param {Point[][]} points
 * @param {AABB} aabb
 * @param {string} teamId
 */
export async function updateGeofence(id, name, points, aabb, teamId) {
  const res = await fetchAPI(
    `/admin/geozones/detail/${teamId}/${id}`,
    METHODS.put,
    {
      name,
      points,
      aabb,
    }
  );
  await assertErrors(res);
}

/**
 * Delete a geofence.
 * @param {string} id
 * @param {string} teamId
 */
export async function deleteGeofence(id, teamId) {
  await fetchAPI(`/admin/geozones/${teamId}/${id}`, METHODS.delete);
}

/**
 * Invite multiple users to the organization with team id `teamId`.
 * @param {string} teamId
 * @param {UploadUser[]} users
 * @returns Promise<void>
 */
export async function importUsers(teamId, users) {
  const res = await fetchAPI(`/admin/import/users/${teamId}`, METHODS.post, {
    users,
  });
  if (res.status !== 200) {
    throw new Error(
      `Fetch admin/import/users/${teamId} received error: [${res.status}] ${res.statusText}`
    );
  }
}

/** Import a chunk of resolved memberships for the super-admin user importer. */
export async function importUsersBulk(users, importId) {
  const res = await fetchAPI('/admin/import/users', METHODS.post, {
    users,
    importId,
  });
  await assertErrors(res);
  const { results } = await parseBody(res);
  return results;
}

/**
 * @typedef {object} Settings
 * @param {boolean} addAdmin
 * @param {string | null} setSubscription
 */

/**
 * Invite multiple organizations to careit.
 * @param {UploadOrg[]} users
 * @param {boolean | null} sendEmail Defaults to true.
 * @param {string | null} emailBody Defaults to the standard invite email.
 * @param {string | null} linkedCorp Team id of coporate account to link all uploaded orgs to. Set to null to disable.
 * @param {Settings} settings Various import settings.
 * @returns Promise<void>
 */
export async function importOrgs(
  orgs,
  sendEmail,
  emailBody,
  linkedCorp,
  settings
) {
  const res = await fetchAPI('/admin/import/orgs', METHODS.post, {
    orgs,
    sendEmail,
    emailBody,
    linkedCorp,
    settings,
  });

  const { result } = await parseBody(res);

  return result;
}

/**
 * Resend an invite to an invited team user.
 * @param {string} userId
 * @param {string} teamId
 */
export async function resendInvite(userId, teamId) {
  await fetchAPI('/admin/resend-invite', METHODS.post, { userId, teamId });
}

/**
 * Update a user's email.
 * NOTE: This does not send another invite to the user in any case.
 * @param {string} userId
 * @param {string} email
 */
export async function updateUserEmail(userId, email) {
  await fetchAPI(`/admin/update-email/${userId}`, METHODS.put, { email });
}

/**
 * Get all teams the current signed in user is a part of if no userId is mentioned otherwise returns teams of userId.
 * @param {string} userId
 * @returns Promise<Team[]>
 */
export async function getTeamsOfUser(userId) {
  const res = await fetchAPI(`/admin/user/teams/${userId}`, METHODS.get);
  const { teams } = await parseBody(res);
  return teams;
}

/**
 * Get activity for a specific user (admin scope)
 */
export async function getUserActivity(userId, { offset, step }) {
  const res = await fetchAPI(
    `/admin/user/${userId}/activity?${qs.stringify({ offset, step })}`,
    METHODS.get
  );
  return await parseBody(res);
}

export async function getUserSupportData(userId, { offset, step } = {}) {
  const query = qs.stringify({ offset, step });
  const res = await fetchAPI(
    `/admin/user/${userId}/support${query ? `?${query}` : ''}`,
    METHODS.get
  );
  return await parseBody(res);
}

export async function getUserPushNotifications(
  userId,
  { offset = 0, limit = 50, filterModel = { items: [] }, sortModel = [] } = {}
) {
  const res = await fetchAPI(
    `/admin/user/${userId}/push-notifications/history?${qs.stringify({ offset, limit })}`,
    METHODS.post,
    { filterModel, sortModel }
  );
  return await parseBody(res);
}

export async function sendUserPushNotification(userId, payload) {
  const res = await fetchAPI(
    `/admin/user/${userId}/push-notifications`,
    METHODS.post,
    payload
  );
  return await parseBody(res);
}

export async function acceptUserInvite(userId, teamId) {
  const res = await fetchAPI(
    `/admin/user/${userId}/support/accept-invite`,
    METHODS.post,
    { teamId }
  );
  return await parseBody(res);
}

export async function acceptAllUserInvites(userId) {
  const res = await fetchAPI(
    `/admin/user/${userId}/support/accept-all-invites`,
    METHODS.post
  );
  return await parseBody(res);
}

/**
 * Get driver availability for a user within a team (admin scope)
 */
export async function getUserDriverAvailability(userId, teamId) {
  const res = await fetchAPI(
    `/admin/user/${userId}/${teamId}/driver-availability`,
    METHODS.get
  );
  const { availability } = await parseBody(res);
  return availability;
}

/**
 * Get notification settings for a user within a team (admin scope)
 */
export async function getUserNotificationSettings(userId, teamId) {
  const res = await fetchAPI(
    `/admin/user/${userId}/${teamId}/notification-settings`,
    METHODS.get
  );
  const { settings } = await parseBody(res);
  console.log('NORMAL SETTINGS', settings);
  return settings;
}

/**
 * Update notification settings for a user within a team (admin scope)
 * Admin-scoped for updating settings of other users. Prefer this within Admin UI.
 */
export async function updateUserNotificationSettings(userId, teamId, settings) {
  const res = await fetchAPI(
    `/admin/user/${userId}/${teamId}/notification-settings`,
    METHODS.post,
    settings
  );
  await assertErrors(res);
}

/**
 * Get detailed user profile fields for a user within a team (admin scope)
 */
export async function getUserDetails(userId, teamId) {
  const res = await fetchAPI(
    `/admin/user/${userId}/${teamId}/details`,
    METHODS.get
  );
  const { user } = await parseBody(res);
  return user;
}

/**
 * Get all corporate accounts.
 * @returns CorporateAccount[]
 */
export async function getAllCorporateAccounts() {
  const res = await fetchAPI(`/admin/corporate-orgs`, METHODS.get);
  const { orgs } = await parseBody(res);
  return orgs;
}

/**
 * Get teams that aren't part of existing team list in coporate account.
 * @param {string} param0.teamId
 * @param {string} param0.name
 */
export async function getEligibleTeamsToAddToCoporateAccount({
  teamId,
  name = '',
}) {
  const res = await fetchAPI(
    `/admin/corporate-org/teams-to-add/${teamId}?${qs.stringify({ name })}`,
    METHODS.get
  );
  const { teams } = await parseBody(res);
  return teams;
}

/**
 * Add a corporate account.
 * This will also create a new admin user. They will get an email instructing
 * them how to access their new account.
 * @param {string} param0.name
 * @param {string} param0.email
 * @param {string} param0.businessEmail
 * @param {string} param0.businessName
 * @param {string} param0.userFirstName
 * @param {string} param0.userLastName
 * @param {string} param0.userEmail
 * @param {string} param0.description
 * @param {string} param0.country
 * @param {string} param0.province
 * @param {string} param0.timezone_id
 */
export async function addCoporateAccount({
  name,
  legalName,
  email,
  userFirstName,
  userLastName,
  userEmail,
  description,
  country,
  province,
  timezone_id,
}) {
  await fetchAPI(`/admin/corporate-org`, METHODS.post, {
    name,
    legalName,
    email,
    userFirstName,
    userLastName,
    userEmail,
    description,
    country,
    province,
    timezone_id,
  });
}

export async function getCorpTeams(teamId) {
  const res = await fetchAPI(
    `/admin/corporate-org/teams/${teamId}`,
    METHODS.get
  );

  const { teams } = await parseBody(res);
  return teams;
}

export async function getCorporateOnboardingIntakeRequests({
  status,
  corporateTeamId,
  requestId,
} = {}) {
  const query = qs.stringify({
    status,
    corporateTeamId,
    requestId,
  });
  const res = await fetchAPI(
    `/admin/onboarding-intake${query ? `?${query}` : ''}`,
    METHODS.get
  );
  const { requests } = await parseBody(res);
  return requests;
}

export async function approveCorporateOnboardingIntakeRequest(
  id,
  decisionReason
) {
  const res = await fetchAPI(
    `/admin/onboarding-intake/${id}/approve`,
    METHODS.post,
    { decisionReason }
  );
  return parseBody(res);
}

export async function rejectCorporateOnboardingIntakeRequest(
  id,
  decisionReason
) {
  const res = await fetchAPI(
    `/admin/onboarding-intake/${id}/reject`,
    METHODS.post,
    { decisionReason }
  );
  return parseBody(res);
}

export async function getCorporateOnboardingIntakeNotes(id) {
  const res = await fetchAPI(
    `/admin/onboarding-intake/${id}/notes`,
    METHODS.get
  );
  const { notes } = await parseBody(res);
  return notes;
}

export async function addCorporateOnboardingIntakeNote(id, body) {
  const res = await fetchAPI(
    `/admin/onboarding-intake/${id}/notes`,
    METHODS.post,
    {
      body,
    }
  );
  const { notes } = await parseBody(res);
  return notes;
}

export async function getCorporateOnboardingOverview(teamId) {
  const res = await fetchAPI(
    `/admin/corporate-org/${teamId}/onboarding-overview`,
    METHODS.get
  );
  return parseBody(res);
}

export async function addCorpTeam({ corpTeamId, teamId }) {
  const res = await fetchAPI(
    `/admin/corporate-org/team/${corpTeamId}/${teamId}`,
    METHODS.post
  );

  if (res.status !== 200) {
    let body;
    try {
      body = await res.json();
    } catch {
      throw new Error(res.statusText);
    }

    if (body.error) {
      throw new Error(body.message);
    } else {
      throw new Error(res.statusText);
    }
  }
}

export async function addCorpTeams({ corpTeamId, teamIds }) {
  const res = await fetchAPI(
    `/admin/corporate-org/teams/${corpTeamId}`,
    METHODS.post,
    { teamIds }
  );

  if (res.status !== 200) {
    let body;
    try {
      body = await res.json();
    } catch {
      throw new Error(res.statusText);
    }

    if (body.error) {
      throw new Error(body.message);
    } else {
      throw new Error(res.statusText);
    }
  }
}

export async function removeCorpTeam({ corpTeamId, teamId }) {
  const res = await fetchAPI(
    `/admin/corporate-org/team/${corpTeamId}/${teamId}`,
    METHODS.delete
  );

  if (res.status !== 200) {
    let body;
    try {
      body = await res.json();
    } catch {
      throw new Error(res.statusText);
    }

    if (body.error) {
      throw new Error(body.message);
    } else {
      throw new Error(res.statusText);
    }
  }
}

/**
 * Estimate the zip/postal code for the given address.
 * Returns null if a zip/postal code cannot be resolved for the address.
 * @param {string} param0.streetAddress
 * @param {string} param0.city
 * @param {string} param0.state
 * @param {string} param0.country
 * @returns Promise<string | null>
 */
export async function estimateZipPostalCode({
  streetAddress,
  city,
  state,
  country,
}) {
  const res = await fetchAPI('/admin/estimate-zip-code', METHODS.post, {
    address: { streetAddress, city, state, country },
  });
  const { zipPostalCode } = await parseBody(res);
  return zipPostalCode;
}

/**
 * Merge one or more organizations into a the given organization (`into`).
 * This will then delete all organizations in the `organizations` list. The
 * procedure is irreversible and may result in data loss. This will throw an
 * error if active subscription plans exist on any of the organizations which
 * will be deleted.
 * @param {string} param.into Team id of organization to have the others merged into.
 * @param {string[]} param.organizations Team ids of organizations to merge into `param.into`.
 * @returns {Promise<void>}
 */
export async function mergeOrganizations({ into, organizations }) {
  const res = await fetchAPI('/admin/merge/organizations', METHODS.post, {
    into,
    organizations,
  });
  await assertErrors(res);
}

/**
 * Merge one or more users into a primary user (`into`).
 * @param {string} param.into User id to merge into (primary)
 * @param {string[]} param.users Secondary user ids to merge into `into`
 */
export async function mergeUsers({ into, users }) {
  const res = await fetchAPI('/admin/merge/users', METHODS.post, {
    into,
    users,
  });
  await assertErrors(res);
}

/**
 * Count resources owned/linked by a user (for merge preview)
 * @param {string} userId
 */
export async function countUser(userId) {
  const res = await fetchAPI(`/admin/count-user/${userId}`, METHODS.get);
  const { count } = await parseBody(res);
  return count;
}

/**
 * @typedef Resources
 * @property {string} id
 * @property {string} name
 * @property {string} formatted_address
 * @property {number} donations
 * @property {number} rescues
 * @property {number} foodRuns
 * @property {number} teamMembers
 * @property {number} geozones
 * @property {number} inspections
 * @property {number} corporateOrgs
 */

/**
 * @typedef {"DONATIONS" | "RESCUES" | "FOOD_RUNS" | "TEAM_MEMBERS" | "GEOZONES" | "INSPECTIONS" | "CORPORATE_ORGS"} Entity
 */

/**
 * Count the amount of `entity` owned by the given organization (by team id).
 * @param {Entity} entity Name of the entity to tally up.
 * @param {string} teamId Team id of organizations to count the resources of.
 * @returns {Promise<number>} The count of the given entity.
 */
export async function count(entity, teamId) {
  const res = await fetchAPI(`/admin/count/${entity}/${teamId}`, METHODS.get);
  const { count } = await parseBody(res);
  return count;
}

/**
 * Count the amount of all resources owned by the given organization (by team id).
 * @param {string} teamId Team id of organizations to count the resources of.
 * @returns {Promise<Resources>}
 */
export async function countAll(teamId) {
  const res = await fetchAPI(`/admin/count/ALL/${teamId}`, METHODS.get);
  const { count } = await parseBody(res);
  return count;
}

/**
 * @typedef UserSessionHistory
 * @property {string} id
 * @property {string} browser_name
 * @property {string} browser_version
 * @property {string} os_name
 * @property {string} os_version
 * @property {string} user_id
 * @property {string} team_id
 * @property {string} event
 * @property {string} ipv4
 * @property {string} ipv6
 * @property {Date} created
 *
 */

/**
 * Gets all user session history
 * @returns Promise<UserSessionHistory[]>
 */
export async function getUserSessionHistory({ offset, step, filterModel }) {
  const res = await fetchAPI(
    `/admin/user-session-history?${qs.stringify({ offset, step })}`,
    METHODS.post,
    { filterModel }
  );
  const { userSessionHistory } = await parseBody(res);
  return userSessionHistory;
}

/**
 * Send email verification link to org admin
 * @param {string} teamId Team id of organization to send verification link.
 * @returns Promise<UserSessionHistory[]>
 */
export async function sendVerificationEmailToOrgAdmin(teamId) {
  const res = await fetchAPI(`/admin/org/verify-email`, METHODS.put, {
    teamId,
  });
  await assertErrors(res);
  return parseBody(res);
}

export async function toggleOrgStatus(args) {
  const res = await fetchAPI(`/admin/toggle-org-status`, METHODS.post, args);
  await assertErrors(res);
  return parseBody(res);
}

export async function toggleOrgActive(args) {
  const res = await fetchAPI(`/admin/toggle-org-active`, METHODS.post, args);
  await assertErrors(res);
  return parseBody(res);
}

export async function bulkSetOrgVisible(teamIds, enabled) {
  const res = await fetchAPI(`/admin/orgs/bulk-visibility`, METHODS.post, {
    teamIds,
    enabled,
  });
  await assertErrors(res);
  return parseBody(res);
}

export async function bulkSetOrgActive(teamIds, enabled) {
  const res = await fetchAPI(`/admin/orgs/bulk-active`, METHODS.post, {
    teamIds,
    enabled,
  });
  await assertErrors(res);
  return parseBody(res);
}

/**
 * @typedef {object} GeocodeResult
 * @property {import('careit/src/address').AddressComponent[]} address_components
 * @property {import('careit/src/address').Geometry} geometry
 * @property {string} formatted_address
 * @property {string} name
 * @property {string} placeId
 */

/**
 * Return all possible geocoding matches fot a given address.
 *
 * This function queries the Google Geocoding API, which is expensive. Use it sparingly.
 *
 * @param {string} address
 * @returns {Promise<GeocodeResult[]>}
 */
export async function geocodingSearch(address) {
  const res = await fetchAPI(
    `/admin/geocode/search?${qs.stringify({ search: address })}`,
    METHODS.get
  );
  await assertErrors(res);
  const { results } = await parseBody(res);
  return results;
}

/**
 * @typedef {Object} AddressComponents
 * @property {number} lat
 * @property {number} lng
 * @property {string} formattedAddress
 * @property {string} streetAddress
 * @property {string} city
 * @property {string} province
 * @property {string} country
 * @property {string} zipCode
 * @property {string} suiteNumber
 * @property {string} timeZoneId
 */

/**
 * Get the address components for a given google place id.
 *
 * Note: This function queries the Google Places API, which is expensive. Use it sparingly.
 *
 * @param {string} placeId
 * @returns {Promise<AddressComponents>}
 */
export async function getAddressComponents(placeId) {
  const res = await fetchAPI(
    `/admin/address-components?placeId=${placeId}`,
    METHODS.get
  );
  await assertErrors(res);
  return parseBody(res);
}

/**
 * @typedef {Object} OrgImportResult
 * @property {string} teamId
 * @property {boolean} didCreate If true, a new organization was created.
 */

/**
 * Try to resolve an organization by it's details. If no organization is found, a new one is created.
 *
 * @param {OrganizationDetails} organization
 * @returns {Promise<OrgImportResult>}
 */
export async function createOrFindOrganization(organization) {
  const res = await fetchAPI(
    `/admin/import/create-find-org`,
    METHODS.post,
    organization
  );
  await assertErrors(res);
  return parseBody(res);
}

/**
 * Resolve imported addresses and rank existing organizations using address,
 * Place ID, proximity and name evidence before deciding whether to create.
 *
 * @param {Array<{id, name, address, street, zip, isNpo}>} orgs
 * @returns {Promise<Array<{id, decision, bestMatch, allMatches, resolvedOrganization, resolutionIssues, reasons}>>}
 */
export async function matchImportOrgs(orgs) {
  const res = await fetchAPI(`/admin/import/match-orgs`, METHODS.post, {
    orgs,
  });
  await assertErrors(res);
  const { results } = await parseBody(res);
  return results;
}

/**
 * @typedef {Object} Donation
 * @property {number} id
 * @property {string} Title
 * @property {Date} Start
 * @property {Date} End
 * @property {Object} '@Donor'
 * @property {string} '@Donor'.id
 * @property {string} '@Donor'.name
 * @property {Object} '@Receiver'
 * @property {string} '@Receiver'.id
 * @property {string} '@Receiver'.name
 * @property {number} 'Non Food Weight'
 * @property {number} 'Non-Perishable Weight'
 * @property {number} 'Dairy Weight'
 * @property {number} 'Produce Weight'
 * @property {number} 'Meat Weight'
 * @property {number} 'Baked Weight'
 * @property {number} 'Beverages Weight'
 * @property {number} 'Prepared Weight'
 * @property {number} 'Frozen Weight'
 * @property {number} 'Compost Weight'
 * @property {number} 'Unusable Weight'
 */

/**
 * @typedef {Object} DonationImportResult
 * @property {number} id  Temporary id of the donation in the import list.
 * @property {string} donationId
 * @property {string} status
 */

/**
 * Import a chunk of donations at a time. It is recommended to import donations in chunks of no more than 100.
 *
 * @param {Donation[]} donations
 * @returns {Promise<DonationImportResult[]>}
 */
export async function createDonations(donations) {
  const res = await fetchAPI(
    `/admin/import/create-donations`,
    METHODS.post,
    donations
  );
  await assertErrors(res);
  const { results } = await parseBody(res);
  return results;
}

export async function deleteAgreementById(id) {
  const res = await fetchAPI(`/admin/agreement/${id}`, METHODS.delete);
  await assertErrors(res);
  return parseBody(res);
}

export async function getAllDocumentsByUser(id) {
  const res = await fetchAPI(`/admin/user/${id}/documents`, METHODS.get);
  await assertErrors(res);
  return parseBody(res);
}

export async function getDeletedOrganizations(data) {
  const res = await fetchAPI(
    `/admin/deleted-orgs?${qs.stringify(data)}`,
    METHODS.get
  );
  await assertErrors(res);
  return parseBody(res);
}

export async function getDeletedDonations(data) {
  const res = await fetchAPI(
    `/admin/deleted-donations?${qs.stringify(data)}`,
    METHODS.get
  );
  await assertErrors(res);
  return parseBody(res);
}

/**
 * Gets the boundary settings for an organization
 * @param {string} orgId - The organization ID
 * @returns {Promise<Object>} The boundary settings
 */
export async function getOrgBoundarySettings(orgId) {
  const res = await fetchAPI(
    `/admin/organizations/${orgId}/boundary-settings`,
    METHODS.get
  );
  return parseBody(res);
}

/**
 * Updates the boundary settings for an organization
 * @param {string} orgId - The organization ID
 * @param {Object} settings - The boundary settings to update
 * @param {number|null} settings.max_delivery_distance - The maximum delivery distance in miles (null to disable)
 * @returns {Promise<Object>} The response
 */
export async function updateOrgBoundarySettings(orgId, settings) {
  const res = await fetchAPI(
    `/admin/organizations/${orgId}/boundary-settings`,
    METHODS.put,
    settings
  );
  return parseBody(res);
}

/**
 * Retrieves inspections for a specific team (organization).
 * @param {string} teamId
 * @param {{offset?: number, step?: number}} [query]
 * @param {object} [filterModel]
 * @param {string[]} [geofences]
 * @returns {Promise<{results: Array, count: number}>}
 */
export async function getTeamInspections(
  teamId,
  query = {},
  filterModel,
  geofences
) {
  const { offset = 0, step = 25 } = query ?? {};
  const queryString = qs.stringify({ offset, step });

  const payload = {};
  if (filterModel) {
    payload.filterModel = filterModel;
  }
  if (geofences) {
    payload.geofences = geofences;
  }

  const res = await fetchAPI(
    `/admin/team/${teamId}/inspections?${queryString}`,
    METHODS.post,
    Object.keys(payload).length ? payload : undefined
  );
  await assertErrors(res);
  const data = await parseBody(res, true);

  const results =
    data?.results ??
    data?.inspections ??
    data?.data ??
    (Array.isArray(data) ? data : []);
  const count = data?.count ?? data?.total ?? results.length;

  return { results, count };
}

export async function getPendingApprovals() {
  const res = await fetchAPI('/admin/approval-queue?step=-1', METHODS.get);
  await assertErrors(res);
  return parseBody(res);
}

export async function getComplaintQueue({
  status,
  step = -1,
  offset = 0,
  searchTerm,
} = {}) {
  const res = await fetchAPI(
    `/admin/complaint-queue?${qs.stringify({ status, step, offset, searchTerm })}`,
    METHODS.get
  );
  await assertErrors(res);
  return parseBody(res);
}

export async function getOpenComplaintCount() {
  const res = await fetchAPI('/admin/complaint-queue/open-count', METHODS.get);
  await assertErrors(res);
  return parseBody(res);
}

export async function getComplaintQueueEntry(id) {
  const res = await fetchAPI(`/admin/complaint-queue/${id}`, METHODS.get);
  await assertErrors(res);
  return parseBody(res);
}

export async function addComplaintQueueNote(id, body) {
  const res = await fetchAPI(
    `/admin/complaint-queue/${id}/notes`,
    METHODS.post,
    { body }
  );
  await assertErrors(res);
  return parseBody(res);
}

export async function updateComplaintQueueStatus(id, status, note = '') {
  const res = await fetchAPI(
    `/admin/complaint-queue/${id}/status`,
    METHODS.post,
    { status, note }
  );
  await assertErrors(res);
  return parseBody(res);
}

export async function getComplaintsByTeam(teamId) {
  const res = await fetchAPI(
    `/admin/complaint-queue/by-team/${teamId}`,
    METHODS.get
  );
  await assertErrors(res);
  return parseBody(res);
}

export async function getApprovalQueueEntry(id) {
  const res = await fetchAPI(`/admin/approval-queue/${id}`, METHODS.get);
  await assertErrors(res);
  return parseBody(res);
}

export async function approveOrganization(id, notes = '') {
  const res = await fetchAPI(
    `/admin/approval-queue/${id}/approve`,
    METHODS.post,
    { notes }
  );
  await assertErrors(res);
  return parseBody(res);
}

export async function declineOrganization(id, declineReason = '', notes = '') {
  const res = await fetchAPI(
    `/admin/approval-queue/${id}/decline`,
    METHODS.post,
    { declineReason, notes }
  );
  await assertErrors(res);
  return parseBody(res);
}

export async function getTeamApprovalStatus(id) {
  const res = await fetchAPI(
    `/admin/approval-queue/team/${id}/status`,
    METHODS.get
  );
  await assertErrors(res);
  return parseBody(res);
}

export async function getApprovedOrganizations({ step = -1, offset = 0 }) {
  const res = await fetchAPI(
    `/admin/approval-queue/approved?${qs.stringify({ step, offset })}`,
    METHODS.get
  );
  await assertErrors(res);
  return parseBody(res);
}

export async function getDeclinedOrganizations({ step = -1, offset = 0 }) {
  const res = await fetchAPI(
    `/admin/approval-queue/declined?${qs.stringify({ step, offset })}`,
    METHODS.get
  );
  await assertErrors(res);
  return parseBody(res);
}

export async function getPendingOrgUpdates({ step = -1, offset = 0 } = {}) {
  const res = await fetchAPI(
    `/admin/org-update-queue?${qs.stringify({ step, offset })}`,
    METHODS.get
  );
  await assertErrors(res);
  return parseBody(res);
}

export async function getApprovedOrgUpdates({ step = -1, offset = 0 } = {}) {
  const res = await fetchAPI(
    `/admin/org-update-queue/approved?${qs.stringify({ step, offset })}`,
    METHODS.get
  );
  await assertErrors(res);
  return parseBody(res);
}

export async function getDeclinedOrgUpdates({ step = -1, offset = 0 } = {}) {
  const res = await fetchAPI(
    `/admin/org-update-queue/declined?${qs.stringify({ step, offset })}`,
    METHODS.get
  );
  await assertErrors(res);
  return parseBody(res);
}

export async function getOrgUpdateQueueEntry(id) {
  const res = await fetchAPI(`/admin/org-update-queue/${id}`, METHODS.get);
  await assertErrors(res);
  return parseBody(res);
}

export async function approveOrgUpdate(id, notes = '') {
  const res = await fetchAPI(
    `/admin/org-update-queue/${id}/approve`,
    METHODS.post,
    { notes }
  );
  await assertErrors(res);
  return parseBody(res);
}

export async function declineOrgUpdate(id, declineReason = '', notes = '') {
  const res = await fetchAPI(
    `/admin/org-update-queue/${id}/decline`,
    METHODS.post,
    { declineReason, notes }
  );
  await assertErrors(res);
  return parseBody(res);
}

export async function getAllInventoryItems({ offset, step, filterModel }) {
  const res = await fetchAPI(
    `/admin/inventory-items?${qs.stringify({ offset, step })}`,
    METHODS.post,
    { filterModel }
  );
  const { items, count } = await parseBody(res);
  return { items, count };
}

export async function getEinStatusReport({
  offset,
  step,
  filterModel,
  sortModel,
  problemsOnly = true,
  activeOnly = true,
  quickFilter = null,
}) {
  const res = await fetchAPI(
    `/admin/ein-status?${qs.stringify({
      offset,
      step,
      problemsOnly,
      activeOnly,
    })}`,
    METHODS.post,
    { filterModel, sortModel, quickFilter }
  );
  const { results, count, summary } = await parseBody(res);
  return { results: results || [], count: count || 0, summary: summary || {} };
}

export async function recheckOrgEin(teamId) {
  const res = await fetchAPI(
    `/admin/ein-status/${teamId}/recheck`,
    METHODS.post,
    {}
  );
  await assertErrors(res);
  return await parseBody(res);
}

/**
 * An org's IRS data: `{ ein, current, history, filings, filingsBackfilledAt,
 * filingFields }` (history newest first, filings per form).
 */
export async function getOrgIrsRecord(teamId) {
  const res = await fetchAPI(
    `/admin/ein-status/${teamId}/irs-record`,
    METHODS.get
  );
  await assertErrors(res);
  return parseBody(res);
}

/** The org's CRM company, created and linked to the org if it has none. */
export async function ensureOrgCrmCompany(teamId) {
  const res = await fetchAPI(
    `/admin/ein-status/${teamId}/crm-company`,
    METHODS.post,
    {}
  );
  await assertErrors(res);
  return parseBody(res);
}

/** Starts pulling the org's IRS filing history now (runs in the background). */
export async function backfillOrgIrsFilings(teamId) {
  const res = await fetchAPI(
    `/admin/ein-status/${teamId}/irs-filings/backfill`,
    METHODS.post,
    {}
  );
  await assertErrors(res);
  return parseBody(res);
}

export async function setOrgEinOverride({ teamId, override = true, reason }) {
  const res = await fetchAPI(
    `/admin/ein-status/${teamId}/override`,
    METHODS.post,
    { override, reason }
  );
  await assertErrors(res);
  return await parseBody(res);
}

/**
 * Platform-wide cancellation log for super admin / support.
 * Ordered by most recent first.
 */
export async function getAdminCancellations({
  offset,
  step,
  filterModel,
  sortModel,
}) {
  const res = await fetchAPI(
    `/admin/cancellations?${qs.stringify({ offset, step })}`,
    METHODS.post,
    { filterModel, sortModel }
  );
  const { results, count } = await parseBody(res);
  return { results: results || [], count: count || 0 };
}

export async function getMarketplaceStats() {
  const res = await fetchAPI(`/admin/marketplace/stats`, METHODS.get);
  return await parseBody(res);
}

export async function getMarketplaceTrend(days) {
  const res = await fetchAPI(
    `/admin/marketplace/stats/trend?${qs.stringify({ days })}`,
    METHODS.get
  );
  return await parseBody(res);
}

export async function getMarketplaceOrders({
  offset = 0,
  step = 25,
  filterModel,
} = {}) {
  const res = await fetchAPI(
    `/admin/marketplace/orders?${qs.stringify({ offset, step })}`,
    METHODS.post,
    { filterModel }
  );
  return await parseBody(res);
}

export async function getMarketplaceAllowedStates() {
  const res = await fetchAPI('/admin/marketplace/allowed-states', METHODS.get);
  const { states } = await parseBody(res);
  return states;
}

export async function addMarketplaceAllowedState(code) {
  const res = await fetchAPI(
    '/admin/marketplace/allowed-states',
    METHODS.post,
    { code }
  );
  const { states } = await parseBody(res);
  return states;
}

export async function removeMarketplaceAllowedState(code) {
  const res = await fetchAPI(
    `/admin/marketplace/allowed-states/${encodeURIComponent(code)}`,
    METHODS.delete
  );
  const { states } = await parseBody(res);
  return states;
}

export async function getMarketplaceAllowedCountries() {
  const res = await fetchAPI(
    '/admin/marketplace/allowed-countries',
    METHODS.get
  );
  const { countries } = await parseBody(res);
  return countries;
}

export async function addMarketplaceAllowedCountry(code) {
  const res = await fetchAPI(
    '/admin/marketplace/allowed-countries',
    METHODS.post,
    { code }
  );
  const { countries } = await parseBody(res);
  return countries;
}

export async function removeMarketplaceAllowedCountry(code) {
  const res = await fetchAPI(
    `/admin/marketplace/allowed-countries/${encodeURIComponent(code)}`,
    METHODS.delete
  );
  const { countries } = await parseBody(res);
  return countries;
}

export async function getMarketplaceListings({
  offset = 0,
  step = 25,
  filterModel,
} = {}) {
  const res = await fetchAPI(
    `/admin/marketplace/listings?${qs.stringify({ offset, step })}`,
    METHODS.post,
    { filterModel }
  );
  return await parseBody(res);
}

export async function getMarketplaceOrderDetail(id) {
  const res = await fetchAPI(`/admin/marketplace/orders/${id}`, METHODS.get);
  return await parseBody(res);
}

export async function refundMarketplaceOrder(id, payload = {}) {
  const res = await fetchAPI(
    `/admin/marketplace/orders/${id}/refund`,
    METHODS.post,
    payload
  );
  return await parseBody(res);
}

export async function cancelMarketplaceOrder(id) {
  const res = await fetchAPI(
    `/admin/marketplace/orders/${id}/cancel`,
    METHODS.post
  );
  return await parseBody(res);
}

export async function resyncMarketplaceOrder(id) {
  const res = await fetchAPI(
    `/admin/marketplace/orders/${id}/resync`,
    METHODS.post
  );
  return await parseBody(res);
}

export async function takedownMarketplaceListing(id, payload) {
  const res = await fetchAPI(
    `/admin/marketplace/listings/${id}/takedown`,
    METHODS.post,
    payload
  );
  return await parseBody(res);
}

export async function getMarketplaceRecallImpact(id, params = {}) {
  const { saleStart, saleEnd } = params || {};
  const queryObj = {};
  if (saleStart) {
    queryObj.saleStart = saleStart;
  }
  if (saleEnd) {
    queryObj.saleEnd = saleEnd;
  }
  const queryString = qs.stringify(queryObj);
  const res = await fetchAPI(
    `/admin/marketplace/listings/${id}/recall-impact${
      queryString ? `?${queryString}` : ''
    }`,
    METHODS.get
  );
  return await parseBody(res);
}

export async function recallMarketplaceListing(id, payload) {
  const res = await fetchAPI(
    `/admin/marketplace/listings/${id}/recall`,
    METHODS.post,
    payload
  );
  return await parseBody(res);
}

export async function getMarketplaceSellerApplications({
  status,
  step,
  offset,
} = {}) {
  const res = await fetchAPI(
    `/admin/marketplace/seller-applications?${qs.stringify({
      status,
      step,
      offset,
    })}`,
    METHODS.get
  );
  return await parseBody(res);
}

export async function approveMarketplaceSeller(teamId) {
  const res = await fetchAPI(
    `/admin/marketplace/seller-applications/${teamId}/approve`,
    METHODS.post
  );
  return await parseBody(res);
}

export async function denyMarketplaceSeller(teamId, payload) {
  const res = await fetchAPI(
    `/admin/marketplace/seller-applications/${teamId}/deny`,
    METHODS.post,
    payload
  );
  return await parseBody(res);
}

export async function suspendMarketplaceSeller(teamId, payload) {
  const res = await fetchAPI(
    `/admin/marketplace/seller-applications/${teamId}/suspend`,
    METHODS.post,
    payload
  );
  return await parseBody(res);
}

export async function reinstateMarketplaceSeller(teamId) {
  const res = await fetchAPI(
    `/admin/marketplace/seller-applications/${teamId}/reinstate`,
    METHODS.post
  );
  return await parseBody(res);
}

export async function getMarketplaceIssues({
  status,
  step = 25,
  offset = 0,
} = {}) {
  const res = await fetchAPI(
    `/admin/marketplace/issues?${qs.stringify({ status, step, offset })}`,
    METHODS.get
  );
  return await parseBody(res);
}

export async function resolveMarketplaceIssue(id, payload) {
  const res = await fetchAPI(
    `/admin/marketplace/issues/${id}/resolve`,
    METHODS.post,
    payload
  );
  return await parseBody(res);
}

export async function getMarketplaceStateInterest() {
  const res = await fetchAPI('/admin/marketplace/state-interest', METHODS.get);
  return await parseBody(res);
}

export async function getMarketplaceReconciliation(start, end) {
  const res = await fetchAPI(
    `/admin/marketplace/reconciliation?${qs.stringify({ start, end })}`,
    METHODS.get
  );
  return await parseBody(res);
}

export async function getMarketplaceLedger(start, end) {
  const res = await fetchAPI(
    `/admin/marketplace/ledger?${qs.stringify({ start, end })}`,
    METHODS.get
  );
  return await parseBody(res);
}

export async function getInventoryItem(id) {
  const res = await fetchAPI(`/admin/inventory-item/${id}`, METHODS.get);
  await assertErrors(res);
  return parseBody(res);
}

export async function getSmsStats() {
  const res = await fetchAPI('/admin/sms/stats', METHODS.get);
  await assertErrors(res);
  return parseBody(res);
}

export async function getSmsMessages(params = {}) {
  const queryString = qs.stringify(params);
  const res = await fetchAPI(`/admin/sms/messages?${queryString}`, METHODS.get);
  await assertErrors(res);
  return parseBody(res);
}

export async function getSmsOrgBreakdown(params = {}) {
  const queryString = qs.stringify(params);
  const res = await fetchAPI(
    `/admin/sms/org-breakdown?${queryString}`,
    METHODS.get
  );
  await assertErrors(res);
  return parseBody(res);
}

export async function getSmsMonthlyCost() {
  const res = await fetchAPI('/admin/sms/monthly-cost', METHODS.get);
  await assertErrors(res);
  return parseBody(res);
}

export async function getSmsBlacklist() {
  const res = await fetchAPI('/admin/sms/blacklist', METHODS.get);
  await assertErrors(res);
  return parseBody(res);
}

export async function removeSmsBlacklistEntry(id) {
  const res = await fetchAPI(`/admin/sms/blacklist/${id}`, METHODS.delete);
  await assertErrors(res);
}

export async function backfillSmsPrices() {
  const res = await fetchAPI('/admin/sms/backfill-prices', METHODS.post);
  await assertErrors(res);
  return parseBody(res);
}

export async function getBackfillStatus() {
  const res = await fetchAPI('/admin/sms/backfill-status', METHODS.get);
  await assertErrors(res);
  return parseBody(res);
}

export async function getPotentialDuplicateOrgs() {
  const res = await fetchAPI('/admin/duplicate-orgs', METHODS.get);
  const { groups } = await parseBody(res);
  return groups;
}

export async function dismissDuplicatePair({ teamId1, teamId2 }) {
  const res = await fetchAPI('/admin/duplicate-orgs/dismiss', METHODS.post, {
    teamId1,
    teamId2,
  });
  await assertErrors(res);
}

export async function dismissDuplicateGroup({ teamIds }) {
  const res = await fetchAPI(
    '/admin/duplicate-orgs/dismiss-group',
    METHODS.post,
    { teamIds }
  );
  await assertErrors(res);
}

export async function undismissDuplicatePair({ teamId1, teamId2 }) {
  const res = await fetchAPI('/admin/duplicate-orgs/undismiss', METHODS.post, {
    teamId1,
    teamId2,
  });
  await assertErrors(res);
}

/**
 * Get admin-only notes for an organization.
 * @param {string} teamId
 * @returns Promise<Note[]>
 */
export async function getAdminOrgNotes(teamId) {
  const res = await fetchAPI(`/admin/org/${teamId}/notes`, METHODS.get);
  const { notes } = await parseBody(res);
  return notes;
}

/**
 * Add an admin-only note to an organization.
 * @param {string} teamId
 * @param {string} text
 */
export async function addAdminOrgNote(teamId, text) {
  await fetchAPI(`/admin/org/${teamId}/notes`, METHODS.post, { text });
}

/**
 * Get role counts for a team.
 * @param {string} teamId
 * @returns Promise<{total: number, admins: number, drivers: number, staff: number}>
 */
/**
 * Get onboarding progress for an organization across all relationships.
 * @param {string} teamId
 * @returns Promise<object[]>
 */
export async function getOrgOnboardingProgress(teamId) {
  const res = await fetchAPI(
    `/admin/org/${teamId}/onboarding-progress`,
    METHODS.get
  );
  const { requests } = await parseBody(res);
  return requests;
}

export async function getTeamRoleCounts(teamId) {
  const res = await fetchAPI(`/admin/team/${teamId}/role-counts`, METHODS.get);
  const { counts } = await parseBody(res);
  return counts;
}
